import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";

import {
    IconButton,
    Typography,
    Grid,
    Chip,
    Collapse,
    Box,
} from "@mui/material";
import {
    FilterList,
} from "@mui/icons-material";

import { useAuthStore } from "../../store/useAuthStore";
import { useErrorStore } from "../../store/useErrorStore";
import { usePosts } from "../../hooks";
import { useFormattedText } from "../../hooks/useFormattedText";

import { apiGetPostThemes, apiGetPostFormats, apiGetDesafios } from "../../services";

import { PostCard } from "./PostCard";
import { Loader } from "../ui/Loader";
import { LinkCard } from "./linkCard";

import { returnEnvironment } from "../../utils";

export const Posts = ({ comunidade }) => {
    const navigate = useNavigate();
    const creator = useAuthStore((state) => state.creator);
    const showError = useErrorStore((state) => state.showError);
    const location = useLocation();
    const activeFilter = location.state?.activeFilter;

    const [filtersOpen, setFiltersOpen] = useState(false);
    const [selectedThemes, setSelectedThemes] = useState([]);
    const [selectedFormats, setSelectedFormats] = useState([]);
    const [selectedDesafios, setSelectedDesafios] = useState([]);
    const [availableThemes, setAvailableThemes] = useState([]);
    const [availableFormats, setAvailableFormats] = useState([]);
    const [availableDesafios, setAvailableDesafios] = useState([]);
    const [showMyPosts, setShowMyPosts] = useState(false);
    const [showILikedPosts, setShowILikedPosts] = useState(false);
    const [mediaType, setMediaType] = useState("");

    const { sanitizeContent, getTruncatedContent } = useFormattedText();

    const renderPostContent = (content, truncate = true, forceWhite = false) => {
        const finalContent = truncate ? getTruncatedContent(content) : content;
        let htmlContent = sanitizeContent(finalContent || "");
        if (forceWhite) {
            htmlContent = htmlContent.replace(/color\s*:\s*[^;"']+;?/gi, "");
        }
        return (
            <Box
                dangerouslySetInnerHTML={{ __html: htmlContent }}
                sx={{
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    mt: 1,
                    "& p": { margin: 0, color: "inherit" },
                    "& *": {
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        color: "inherit",
                    },
                }}
            />
        );
    };

    const {
        data: postsData,
        fetchNextPage,
        hasNextPage,
        isLoading,
        error,
    } = usePosts(comunidade?.id, {
        author: showMyPosts ? creator.id : null,
        iLiked: showILikedPosts ? creator.id : null,
        themes: selectedThemes.length > 0 ? selectedThemes : null,
        formats: selectedFormats.length > 0 ? selectedFormats : null,
        desafios: selectedDesafios.length > 0 ? selectedDesafios : null,
        type: mediaType || null,
        status: "APROVADO",
    });

    const posts = useMemo(() => postsData?.pages?.flatMap((page) => page.posts) ?? [], [postsData]);
    const pinnedPosts = posts.filter((post) => post.pinado === true);
    const [currentPlayingVideo, setCurrentPlayingVideo] = useState(null);
    const videoObserver = useRef(null);

    const handlePostClick = (post) => {
        navigate(`/post/${post.id}`);
    };

    const handleThemeChange = (theme) => {
        setSelectedThemes((prev) =>
            prev.includes(theme) ? prev.filter((t) => t !== theme) : [...prev, theme],
        );
    };

    const handleFormatChange = (format) => {
        setSelectedFormats((prev) =>
            prev.includes(format) ? prev.filter((f) => f !== format) : [...prev, format],
        );
    };

    const handleDesafioChange = (desafioId) => {
        setSelectedDesafios((prev) =>
            prev.includes(desafioId) ? prev.filter((d) => d !== desafioId) : [...prev, desafioId],
        );
    };

    const handleMediaTypeChange = (event, newType) => {
        setMediaType(mediaType === newType ? null : newType);
    };

    const handleToggleFilters = () => {
        setFiltersOpen(!filtersOpen);
    };

    const hasActiveFilters = useMemo(() => {
        return selectedThemes.length > 0 || selectedFormats.length > 0 || selectedDesafios.length > 0 || showMyPosts || showILikedPosts || mediaType !== "";
    }, [selectedThemes, selectedFormats, selectedDesafios, showMyPosts, showILikedPosts, mediaType]);

    if (error) return <p>Erro ao carregar posts.</p>;

    useEffect(() => {
        const fetchThemesAndFormats = async () => {
            try {
                const [themesResponse, formatsResponse, desafiosResponse] = await Promise.all([
                    apiGetPostThemes(),
                    apiGetPostFormats(),
                    apiGetDesafios({ comunidade: comunidade?.id }),
                ]);
                setAvailableThemes(themesResponse || []);
                setAvailableFormats(formatsResponse || []);
                setAvailableDesafios(desafiosResponse || []);
            } catch (error) {
                showError(error);
            }
        };

        fetchThemesAndFormats();
    }, [showError, comunidade?.id]);

    useEffect(() => {
        setShowMyPosts(activeFilter === "meus-posts");
        setShowILikedPosts(activeFilter === "curtidas");
    }, [activeFilter]);

    return (
        <div style={{ position: "relative", minHeight: "100vh" }}>
            <Box sx={{ width: "100%" }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        mb: 1,
                        pb: 0,
                        borderBottom: "1px solid var(--primary-color)",
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 500,
                            color: "var(--primary-color)",
                        }}
                    >
                        Posts da Comunidade
                    </Typography>

                    <IconButton
                        onClick={handleToggleFilters}
                        sx={{
                            color: hasActiveFilters ? "var(--primary-color)" : "inherit",
                            transform: filtersOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "all 0.3s ease",
                            ...(hasActiveFilters && {
                                background: "rgba(255, 255, 255, 0.1)",
                                backdropFilter: "blur(10px)",
                                WebkitBackdropFilter: "blur(10px)",
                                border: "1px solid rgba(255, 255, 255, 0.18)",
                                boxShadow: "0 4px 12px 0 rgba(31, 38, 135, 0.15)",
                                "&:hover": {
                                    background: "rgba(255, 255, 255, 0.15)",
                                },
                            }),
                        }}
                    >
                        <FilterList />
                    </IconButton>
                </Box>

                <Collapse in={filtersOpen} sx={{ width: "100%" }}>
                    <Box
                        sx={{
                            p: 2.5,
                            background: "rgba(255, 255, 255, 0.15)",
                            backdropFilter: "blur(10px)",
                            WebkitBackdropFilter: "blur(10px)",
                            borderRadius: 2,
                            border: "1px solid rgba(255, 255, 255, 0.18)",
                            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
                            position: "relative",
                            "&:before": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background:
                                    "linear-gradient(128deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.06) 100%)",
                                borderRadius: 2,
                                zIndex: -1,
                            },
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 2,
                                fontWeight: 500,
                                color: "var(--primary-color)",
                            }}
                        >
                            Filtrar Posts
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 1,
                                color: "var(--primary-dark-color)",
                                fontWeight: 500,
                            }}
                        >
                            Temas:
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                            {availableThemes?.map((theme) => (
                                <CustomChip
                                    key={theme}
                                    label={theme}
                                    onClick={() => handleThemeChange(theme)}
                                    isSelected={selectedThemes.includes(theme)}
                                />
                            ))}
                        </Box>

                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 1,
                                color: "var(--primary-dark-color)",
                                fontWeight: 500,
                            }}
                        >
                            Formatos:
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                            {availableFormats?.map((format) => (
                                <CustomChip
                                    key={format}
                                    label={format}
                                    onClick={() => handleFormatChange(format)}
                                    isSelected={selectedFormats.includes(format)}
                                />
                            ))}
                        </Box>

                        {availableDesafios?.length > 0 && (
                            <>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mb: 1,
                                        color: "var(--primary-dark-color)",
                                        fontWeight: 500,
                                    }}
                                >
                                    Desafios:
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                                    {availableDesafios.map((desafio) => (
                                        <CustomChip
                                            key={desafio.id}
                                            label={desafio.titulo || desafio.nome || `Desafio ${desafio.id}`}
                                            onClick={() => handleDesafioChange(desafio.id)}
                                            isSelected={selectedDesafios.includes(desafio.id)}
                                        />
                                    ))}
                                </Box>
                            </>
                        )}

                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 1,
                                color: "var(--primary-dark-color)",
                                fontWeight: 500,
                            }}
                        >
                            Autor:
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <CustomChip
                                label="Meus Posts"
                                onClick={() => setShowMyPosts(!showMyPosts)}
                                isSelected={showMyPosts}
                            />
                        </Box>

                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 1,
                                color: "var(--primary-dark-color)",
                                fontWeight: 500,
                            }}
                        >
                            Curtida:
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <CustomChip
                                label="Posts que eu curti!"
                                onClick={() => setShowILikedPosts(!showILikedPosts)}
                                isSelected={showILikedPosts}
                            />
                        </Box>

                        {returnEnvironment() !== "wizard" && (
                            <>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mb: 1,
                                        color: "var(--primary-dark-color)",
                                        fontWeight: 500,
                                    }}
                                >
                                    Exibir posts contendo:
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                                    {["Vídeo", "Texto", "Foto", "Link"].map((type) => (
                                        <CustomChip
                                            key={type}
                                            label={type}
                                            onClick={() => handleMediaTypeChange(null, type.toLowerCase())}
                                            isSelected={mediaType === type.toLowerCase()}
                                        />
                                    ))}
                                </Box>
                            </>
                        )}
                    </Box>
                </Collapse>
            </Box>

            {isLoading && <Loader />}
            <InfiniteScroll
                dataLength={posts.length - pinnedPosts.length}
                next={fetchNextPage}
                hasMore={hasNextPage}
                loader={<p>Carregando mais...</p>}
                endMessage={
                    <p style={{ textAlign: "center" }}>
                        <b>Você chegou ao final!</b>
                    </p>
                }
            >
                <Grid container spacing={2} marginTop={0} marginBottom={10}>
                    {pinnedPosts.length > 0 && (
                        <>
                            <Grid item xs={12}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: "bold",
                                        color: "var(--primary-dark-color)",
                                        marginBottom: 2,
                                    }}
                                >
                                    Post Fixado
                                </Typography>
                            </Grid>

                            {pinnedPosts.map((post) => (
                                <Grid item xs={12} key={post.id}>
                                    <PostCard
                                        post={post}
                                        onClick={handlePostClick}
                                        isPinned={true}
                                        currentPlayingVideo={currentPlayingVideo}
                                        setCurrentPlayingVideo={setCurrentPlayingVideo}
                                        videoObserver={videoObserver}
                                        renderPostContent={renderPostContent}
                                        LinkCard={LinkCard}
                                        creator={creator}
                                        showError={showError}
                                    />
                                </Grid>
                            ))}
                        </>
                    )}

                    {posts
                        .filter((post) => !post.pinado)
                        .map((post) => (
                            <Grid item xs={12} key={post.id}>
                                <PostCard
                                    post={post}
                                    onClick={handlePostClick}
                                    isPinned={false}
                                    currentPlayingVideo={currentPlayingVideo}
                                    setCurrentPlayingVideo={setCurrentPlayingVideo}
                                    videoObserver={videoObserver}
                                    renderPostContent={renderPostContent}
                                    LinkCard={LinkCard}
                                    creator={creator}
                                    showError={showError}
                                />
                            </Grid>
                        ))}
                </Grid>
            </InfiniteScroll>
        </div>
    );
};

const CustomChip = ({ label, isSelected, onClick }) => (
    <Chip
        label={label}
        onClick={onClick}
        sx={{
            color: isSelected ? "var(--white-color)" : "var(--newpost-cattegorychiptext-color)",
            bgcolor: isSelected ? "var(--newpost-cattegorychiptextselected-color)" : "var(--newpost-cattegorychip-color)",
            opacity: isSelected ? 1 : 0.6,
            textTransform: "uppercase",
            "&:hover": {
                bgcolor: isSelected ? "var(--newpost-cattegorychiptextselected-color)" : "var(--newpost-cattegorychip-color)",
                opacity: isSelected ? 1 : 0.8,
            },
            "&:active": {
                bgcolor: isSelected ? "var(--newpost-cattegorychiptextselected-color)" : "var(--newpost-cattegorychip-color)",
                opacity: isSelected ? 1 : 0.8,
            },
        }}
    />
);
